import { useEffect, useMemo, useState } from "react";

function readViews(storageKey) {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return { views: [], defaultViewId: "" };
        const parsed = JSON.parse(raw);
        return {
            views: Array.isArray(parsed.views) ? parsed.views : [],
            defaultViewId: parsed.defaultViewId || "",
        };
    } catch {
        return { views: [], defaultViewId: "" };
    }
}

function writeViews(storageKey, value) {
    localStorage.setItem(storageKey, JSON.stringify(value));
}

function clone(value) {
    return JSON.parse(JSON.stringify(value ?? {}));
}

export default function useSavedViews(storageKey, state, applyState, defaultState) {
    const [ready, setReady] = useState(false);
    const [viewsState, setViewsState] = useState({ views: [], defaultViewId: "" });
    const [activeViewId, setActiveViewId] = useState("");

    useEffect(() => {
        const next = readViews(storageKey);
        setViewsState(next);
        setActiveViewId(next.defaultViewId || "");
        setReady(true);
    }, [storageKey]);

    useEffect(() => {
        if (!ready) return;
        writeViews(storageKey, viewsState);
    }, [ready, storageKey, viewsState]);

    const currentView = useMemo(() => viewsState.views.find((v) => v.id === activeViewId) || null, [activeViewId, viewsState.views]);

    const createView = () => {
        const name = window.prompt("Name this view", currentView?.name || "My View");
        if (!name) return;
        const view = {
            id: crypto.randomUUID(),
            name: name.trim(),
            state: clone(state),
        };
        setViewsState((prev) => ({ ...prev, views: [...prev.views, view] }));
        setActiveViewId(view.id);
    };

    const renameView = () => {
        if (!currentView) return;
        const name = window.prompt("Rename view", currentView.name);
        if (!name) return;
        setViewsState((prev) => ({
            ...prev,
            views: prev.views.map((v) => v.id === currentView.id ? { ...v, name: name.trim() } : v),
        }));
    };

    const deleteView = () => {
        if (!currentView) return;
        if (!window.confirm(`Delete view "${currentView.name}"?`)) return;
        setViewsState((prev) => {
            const nextViews = prev.views.filter((v) => v.id !== currentView.id);
            const nextDefault = prev.defaultViewId === currentView.id ? "" : prev.defaultViewId;
            return { views: nextViews, defaultViewId: nextDefault };
        });
        setActiveViewId("");
    };

    const setDefaultView = () => {
        if (!currentView) return;
        setViewsState((prev) => ({ ...prev, defaultViewId: currentView.id }));
    };

    const applyView = (id) => {
        const view = viewsState.views.find((v) => v.id === id);
        if (!view) return;
        setActiveViewId(id);
        applyState(clone(view.state || defaultState));
    };

    const resetViews = () => {
        setActiveViewId("");
        applyState(clone(defaultState));
    };

    const options = useMemo(() => viewsState.views.map((v) => ({ value: v.id, label: `${v.name}${viewsState.defaultViewId === v.id ? " (default)" : ""}` })), [viewsState.defaultViewId, viewsState.views]);

    return {
        ready,
        views: viewsState.views,
        activeViewId,
        currentView,
        options,
        setActiveViewId: (id) => {
            setActiveViewId(id);
            if (id) applyView(id);
        },
        createView,
        renameView,
        deleteView,
        setDefaultView,
        applyView,
        resetViews,
    };
}
